import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppLayout from '../../../component/appLayout';
import { Heading, Content, TimeLineCard, RangeSelectionSection, RightSection, ButtonComponent } from './styles';
import { Header, DateDropBox, LeftContent, DropdownButton, HeaderLeft, HeaderTitle } from '../styles';
import { DateRange } from 'react-date-range';
import {
  BUTTON_TYPE,
  CHIP_TYPE,
  CHIP_SIZE,
  CHIP_ICON_POSITION,
  PREFERENCE_WRITE_DEBOUNCE_MS,
  TIME_DATE_SELECTED_TEXT
} from '../../../global/constants';
import { isEmpty , checkIsAllBoxChecked, getDateRange } from '../../../helpers/common';
import {
  TeamActivityInterface,
  UserCheckListInterface,
  ProjectListInterface,
  ActivitiesResponse
, ReportTimeFilterInterfaceV2 } from '../../../interfaces/ReportInterface';
import { BottomData, CustomItemDiv } from '../styles/styles';
import ReportDurationDropdown from '../../../component/dropdowns/reportDurationDropdown';
import { Dropdown, DropdownItem } from '../../../component/dropdown';
import { observer } from 'mobx-react-lite';
import ResponsiveHeader from '../../../component/responsiveHeader';
import { useMobileDevice } from '../../../global/useMobile';
import BottomSubNavigation from '../../../component/bottomNavigation/subNavigation';
import ReportTimelineLoading from '../../../component/loading/reportTimeLineLoading';
import { getActivities } from '../../../services/reportService';
import { SelectionRangeInterface } from '../../../interfaces/TimeReportInterface';
import ReportTeamTimeLine from './timeLine';
import { getTimelineExportData } from './exportHelpers';
import { rootStore } from '../../../mobx/rootStore';
import { ProjectModel } from '../../../mobx/models/project';
import Button from '../../../component/button';
import { CSVLink } from 'react-csv';
import { TimelineReportCSVHeader } from '../../../global/row';
import { getUserWorkspacePreferenceData, updateUserWorkspacePreference } from '../../../helpers/firebaseHelper';
import Chip from '../../../component/chipComponent';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const TeamTimeline: React.FC = () => {
  //Refs
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const outsideRef = useRef<HTMLDivElement | null>(null);
  //States
  const [loading, setLoading] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [isTextDropdownOpen, setIsTextDropdownOpen] = useState(false);
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [usersList, setUsersList] = useState<UserCheckListInterface[]>([]);
  const [projectList, setProjectList] = useState<ProjectListInterface[]>([]);
  const [selectAllUser, setSelectAllUser] = useState(true);
  const [userSearchResult, setUserSearchResult] = useState<UserCheckListInterface[]>([]);
  const [isUserSearch, setIsUserSearch] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [selectAllProject, setSelectAllProject] = useState(true);
  const [searchProject, setSearchProject] = useState('');
  const [searchProjectResult, setSearchProjectResult] = useState<ProjectListInterface[]>([]);
  const [isProjectSearch, setIsProjectSearch] = useState(false);
  const [activities, setActivities] = useState<TeamActivityInterface[]>([]);
  const [selectedRange, setSelectedRange] = useState<SelectionRangeInterface>({
    startDate: dayjs().startOf('day').toDate(),
    endDate: dayjs().endOf('day').toDate(),
    key: 'selection'
  });
  const [selectedText, setSelectedText] = useState('Custom');
  const [dataLoading, setDataLoading] = useState(false);
  const [timeline_filter, set_timeline_filter] = useState<ReportTimeFilterInterfaceV2>();
  const [hasMore, setHasMore] = useState(true);
  //Display load more button
  const showLoadMoreButton = hasMore && activities.length > 0;

  // Mobx variables
  const { projectStore, reportStore, workspaceStore } = rootStore;
  const { projects } = projectStore || {};
  const { currentWorkspace: workspace } = workspaceStore;
  const workspace_id = workspace?.id || '';

  // Other variables
  const mobile = useMobileDevice();

  // Debounce Firebase preference writes: local state updates immediately for instant UI feedback,
  // while rapid-fire filter toggles (member/project/date) are coalesced into a single Firestore commit.
  // The workspace id is captured at schedule time and passed explicitly, so a pending/flushed write
  // always lands in the workspace that was active when it was scheduled — never the one switched to.
  const debouncedWritePreference = useRef(
    debounce((data: ReportTimeFilterInterfaceV2, workspaceId?: string) => {
      void updateUserWorkspacePreference({ team_timeline_filter: data }, workspaceId);
    }, PREFERENCE_WRITE_DEBOUNCE_MS)
  ).current;

  // Flush any pending preference write on unmount so the last change isn't lost.
  useEffect(() => () => debouncedWritePreference.flush(), [debouncedWritePreference]);

  // Add useEffect for outside click handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;

      if (
        outsideRef.current &&
        !outsideRef.current.contains(targetElement) &&
        !targetElement.closest('.calendar-dropdown')
      ) {
        setIsDateDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const groupedActivities = useMemo(() => {
    if (!activities || activities.length === 0) {
      return {};
    }

    const todayStart = dayjs().startOf('day');
    const weekStart = dayjs().startOf('week');
    const monthStart = dayjs().startOf('month');

    // Define the sections in the order we want them displayed
    const sections: { [key: string]: TeamActivityInterface[] } = {
      Today: [],
      'This Week': [],
      'This Month': [],
      Older: []
    };

    // Group activities by time period
    activities.forEach((activity) => {
      const activityDate = dayjs(activity.createdTime);

      if (activityDate.isSameOrAfter(todayStart, 'day')) {
        sections['Today'].push(activity);
      } else if (activityDate.isSameOrAfter(weekStart)) {
        sections['This Week'].push(activity);
      } else if (activityDate.isSameOrAfter(monthStart)) {
        sections['This Month'].push(activity);
      } else {
        sections['Older'].push(activity);
      }
    });

    // Sort activities within each section by date (most recent first)
    Object.keys(sections).forEach((key) => {
      sections[key].sort((a, b) => dayjs(b.createdTime).diff(dayjs(a.createdTime)));
    });

    // Remove empty sections
    const filteredSections = Object.entries(sections).reduce((acc, [key, value]) => {
      if (value.length > 0) {
        acc[key] = value;
      }
      return acc;
    }, {} as { [key: string]: TeamActivityInterface[] });

    return filteredSections;
  }, [activities]);

  // Flatten the paginated activities into CSV-ready rows for export.
  const exportData = useMemo(
    () => getTimelineExportData(activities, workspace?.users, workspace?.name),
    [activities, workspace?.users, workspace?.name]
  );

  const loadFilterData = useCallback(async () => {
    if (isEmpty(workspace_id)) return;

    try {
      const result = await getUserWorkspacePreferenceData('team_timeline_filter');

      // Generate project and user lists from current workspace data
      const updatedProjectsResponse = projects?.map((item: ProjectModel) => ({
        id: item.id,
        name: item.name,
        checked: true
      }));
      const usersList = workspace?.users?.map((item) => ({ id: item.id, name: item.name, checked: true }));

      // Check for new projects/users that are not in the saved filter
      const hasNewProjects =
        !result?.project_list ||
        updatedProjectsResponse.some(
          (currentProject: { id: string }) =>
            !result.project_list.some((savedProject: { id: string }) => savedProject.id === currentProject.id)
        );

      const hasNewUser =
        !result?.user_list ||
        usersList?.some(
          (currentUser) => !result.user_list.some((savedUser: { id: string }) => savedUser.id === currentUser.id)
        );

      let final_selected_text = result?.selected_text || 'Today';
      let final_selected_range;

      if (final_selected_text !== 'Custom') {
        final_selected_range = getDateRange(final_selected_text);
      } else if (result?.selected_range) {
        final_selected_range = {
          startDate: new Date(result.selected_range.startDate).toISOString(),
          endDate: new Date(result.selected_range.endDate).toISOString(),
          key: result.selected_range.key
        };
      } else {
        final_selected_range = getDateRange('Today');
      }

      const data = {
        ...(result || {}),
        project_list: hasNewProjects ? updatedProjectsResponse : result?.project_list,
        user_list: hasNewUser ? usersList : result?.user_list,
        selected_text: final_selected_text,
        selected_range: final_selected_range,
        selected_all_user: result?.selected_all_user ?? true,
        selected_all_project: result?.selected_all_project ?? true
      };
      // Update component states with loaded/merged data
      set_timeline_filter(data);
      setProjectList(data.project_list || updatedProjectsResponse);
      setUsersList(data.user_list || usersList);
      setSelectAllUser(checkIsAllBoxChecked(data.user_list || usersList));
      setSelectAllProject(checkIsAllBoxChecked(data.project_list || updatedProjectsResponse));
      setSelectedRange({
        startDate: new Date(data.selected_range.startDate),
        endDate: new Date(data.selected_range.endDate),
        key: data.selected_range.key
      });
      setSelectedText(data.selected_text);

      // Persist the potentially updated filter back to Firebase
      debouncedWritePreference(data, workspace_id);

      return data;
    } catch (error) {
      console.error('Error loading timeline filter data:', error);
    }
  }, [projects, workspace?.users, workspace_id, debouncedWritePreference]);

  const updateActivitiesAndState = useCallback((response: ActivitiesResponse) => {
    const newActivities = response?.data?.activities;
    setActivities((prev) => [...(prev || []), ...(newActivities || [])]);
    setHasMore(response?.data?.pageInfo?.hasMore || false);
  }, []);

  const loadData = useCallback(
    async (currentProjects = projectList, currentUsers = usersList, currentRange = selectedRange) => {
      const projectIds = currentProjects?.filter((project) => project.checked)?.map((project) => project.id);
      const userIds = currentUsers?.filter((user) => user.checked)?.map((user) => user.id);
      const startDate = dayjs(currentRange.startDate).startOf('day').toISOString();
      const endDate = dayjs(currentRange.endDate).endOf('day').toISOString();
      try {
        setLoading(true);
        let payload;
        if (!isEmpty(workspace_id)) {
          if (userIds?.length !== 0 || projectIds?.length !== 0) {
            payload = {
              users: userIds,
              projects: projectIds
            };
          } else {
            const projectIds = projectList?.map((project) => project.id);
            const userIds = usersList?.map((user) => user.id);
            payload = {
              users: userIds,
              projects: projectIds
            };
          }

          const response = await getActivities(payload, startDate, endDate);
          if (response) {
            updateActivitiesAndState(response);
          }
        }
      } catch (error) {
        console.log('error', error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    },
    [projectList, usersList, selectedRange, workspace_id, updateActivitiesAndState]
  );

  useEffect(() => {
    const initialLoad = async () => {
      if (!isEmpty(workspace_id)) {
        setLoading(true);
        const filters = await loadFilterData();
        if (filters) {
          await loadData(filters.project_list, filters.user_list, filters.selected_range);
        }
        setLoading(false);
      }
    };
    initialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace_id]);

  useEffect(() => {
    return () => {
      setSearchUser('');
      setSearchProject('');
      setIsProjectSearch(false);
      setIsUserSearch(false);
      setSearchProjectResult([]);
      setUserSearchResult([]);
      setActivities([]);
      reportStore.setReportTimelineNextCursor(null);
    };
  }, [reportStore]);

  const onClickonClient = useCallback((value: boolean) => {
    setIsCustomerDropdownOpen(value);
    setIsProjectDropdownOpen(false);
    setIsDateDropdownOpen(false);
    setIsTextDropdownOpen(false);
  }, []);

  const onClickonProject = useCallback((value: boolean) => {
    setIsProjectDropdownOpen(value);
    setIsCustomerDropdownOpen(false);
    setIsDateDropdownOpen(false);
    setIsTextDropdownOpen(false);
  }, []);

  const openDateDropdown = useCallback((value: boolean) => {
    setIsDateDropdownOpen(value);
    setIsProjectDropdownOpen(false);
    setIsCustomerDropdownOpen(false);
    setIsTextDropdownOpen(false);
  }, []);

  const openTextDropdown = useCallback((value: boolean) => {
    setIsTextDropdownOpen(value);
    setIsDateDropdownOpen(false);
    setIsProjectDropdownOpen(false);
    setIsCustomerDropdownOpen(false);
  }, []);

  const resetActivityData = useCallback(() => {
    setActivities([]);
    rootStore.reportStore.setReportTimelineNextCursor(null);
  }, []);

  const searchQueryUser = useCallback(
    (value: string) => {
      if (!isEmpty(value)) {
        const result =
          usersList?.filter((item: { name: string }) => item?.name?.toLowerCase().includes(value?.toLowerCase())) || [];
        if (result) {
          setUserSearchResult(result);
          return null;
        }
      } else {
        setUserSearchResult([]);
        setIsUserSearch(false);
      }
    },
    [usersList]
  );

  const onChangeSearchUser = useCallback(
    (value: string) => {
      let timeout;
      if (value) {
        setSearchUser(value);
        if (!isUserSearch) setIsUserSearch(true);
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          searchQueryUser(value);
        }, 300);
      } else {
        setSearchUser('');
        if (isUserSearch) setIsUserSearch(false);
        setUserSearchResult([]);
      }
    },
    [isUserSearch, searchQueryUser]
  );

  const onChangeUser = useCallback(
    async (item: string, value: boolean) => {
      let updatedUsersList;
      if (value) {
        updatedUsersList = usersList?.map((ele) => {
          return ele?.id === item ? { ...ele, checked: true } : ele;
        });
      } else {
        updatedUsersList = usersList?.map((ele) => {
          return ele?.id === item ? { ...ele, checked: false } : ele;
        });
      }
      const isAllUsersSelected = updatedUsersList.every((user) => user.checked);
      setSelectAllUser(isAllUsersSelected);
      setIsCustomerDropdownOpen(false);
      setUsersList(updatedUsersList);
      const data = {
        ...(timeline_filter || {}),
        user_list: updatedUsersList,
        selected_all_user: isAllUsersSelected
      };
      set_timeline_filter(data);
      debouncedWritePreference(data, workspace_id);
    },
    [usersList, timeline_filter, workspace_id, debouncedWritePreference]
  );

  const onChangeSelectAllUser = useCallback(
    async (value: boolean) => {
      setSelectAllUser(value);
      const updatedUsersList = usersList?.map((ele) => {
        return { ...ele, checked: value };
      });
      setIsCustomerDropdownOpen(false);
      setUsersList(updatedUsersList);
      const data = {
        ...(timeline_filter || {}),
        user_list: updatedUsersList,
        selected_all_user: value
      };
      set_timeline_filter(data);
      debouncedWritePreference(data, workspace_id);
    },
    [usersList, timeline_filter, workspace_id, debouncedWritePreference]
  );

  const renderCustomerData = useMemo(() => {
    return (
      <BottomData>
        {isEmpty(searchUser) && (
          <DropdownItem
            label='Select All'
            isCheckBox={true}
            onClick={() => onChangeSelectAllUser(!selectAllUser)}
            isChecked={selectAllUser}
          />
        )}
        {(isEmpty(searchUser) ? usersList : userSearchResult)?.map(
          (item: { checked: boolean; id: string; name: string }) => {
            return (
              <DropdownItem
                label={item?.name}
                onClick={() => onChangeUser(item?.id, !item?.checked)}
                isCheckBox={true}
                isChecked={item?.checked}
                key={item?.id}
              />
            );
          }
        )}
      </BottomData>
    );
  }, [searchUser, selectAllUser, usersList, userSearchResult, onChangeSelectAllUser, onChangeUser]);

  const searchProjectQuery = useCallback(
    (value: string) => {
      if (!isEmpty(value)) {
        const result = projectList?.filter((item) => item?.name?.toLowerCase().includes(value?.toLowerCase())) || [];
        if (result) {
          setSearchProjectResult(result);
          return null;
        }
      } else {
        setSearchProjectResult([]);
        setIsProjectSearch(false);
      }
    },
    [projectList]
  );

  const onChangeSearchProject = useCallback(
    (value: string) => {
      let timeout;
      if (value) {
        setSearchProject(value);
        if (!isProjectSearch) setIsProjectSearch(true);
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          searchProjectQuery(value);
        }, 300);
      } else {
        setSearchProject('');
        if (isProjectSearch) setIsProjectSearch(false);
        setSearchProjectResult([]);
      }
    },
    [isProjectSearch, searchProjectQuery]
  );

  const onSelectAllProject = useCallback(
    async (value: boolean) => {
      setSelectAllProject(value);
      setIsProjectDropdownOpen(false);
      const updatedProjectsList = projectList?.map((ele) => {
        return { ...ele, checked: value };
      });
      setProjectList(updatedProjectsList);
      const data = {
        ...(timeline_filter || {}),
        project_list: updatedProjectsList,
        selected_all_project: value
      };
      set_timeline_filter(data);
      debouncedWritePreference(data, workspace_id);
    },
    [projectList, timeline_filter, workspace_id, debouncedWritePreference]
  );

  const onChangeProject = useCallback(
    async (item: string, value: boolean) => {
      let updatedProjectsList;
      if (value) {
        updatedProjectsList = projectList?.map((ele) => {
          return ele?.id === item ? { ...ele, checked: true } : ele;
        });
      } else {
        updatedProjectsList = projectList?.map((ele) => {
          return ele?.id === item ? { ...ele, checked: false } : ele;
        });
      }
      const isAllProjectsSelected = updatedProjectsList.every((project) => project.checked);
      setSelectAllProject(isAllProjectsSelected);
      setProjectList(updatedProjectsList);
      setIsProjectDropdownOpen(false);
      const data = {
        ...(timeline_filter || {}),
        project_list: updatedProjectsList,
        selected_all_project: isAllProjectsSelected
      };
      set_timeline_filter(data);
      debouncedWritePreference(data, workspace_id);
    },
    [projectList, timeline_filter, workspace_id, debouncedWritePreference]
  );

  const renderProjectData = useMemo(() => {
    return (
      <BottomData isBorderRadius>
        {isEmpty(searchProject) && (
          <DropdownItem
            label='Select All'
            isCheckBox={true}
            onClick={() => onSelectAllProject(!selectAllProject)}
            isChecked={selectAllProject}
          />
        )}
        {(isEmpty(searchProject) ? projectList : searchProjectResult)?.map((item) => {
          return (
            <DropdownItem
              label={item?.name}
              onClick={(e) => onChangeProject(item?.id, !item?.checked)}
              isCheckBox={true}
              isChecked={item?.checked}
              key={item?.id}
            />
          );
        })}
      </BottomData>
    );
  }, [searchProject, selectAllProject, projectList, searchProjectResult, onSelectAllProject, onChangeProject]);

  const updateDateRangeChange = useCallback(
    async (startDate: Date, endDate: Date, selected_text: string) => {
      try {
        setSelectedRange({ startDate, endDate, key: 'selection' });
        setSelectedText(selected_text);
        setIsTextDropdownOpen(false);

        const data: ReportTimeFilterInterfaceV2 = {
          ...(timeline_filter || {}),
          selected_range: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            key: 'selection'
          },
          selected_text: selected_text
        };
        set_timeline_filter(data);
        debouncedWritePreference(data, workspace_id);
      } catch (e) {}
    },
    [timeline_filter, workspace_id, debouncedWritePreference]
  );

  const handleSelect = useCallback(
    async (ranges: { selection: { startDate: Date; endDate: Date; key: string } }) => {
      setSelectedRange(ranges.selection);
      setSelectedText(TIME_DATE_SELECTED_TEXT.CUSTOM);
      const data = {
        ...(timeline_filter || {}),
        selected_range: {
          startDate: new Date(ranges.selection.startDate).toISOString(),
          endDate: new Date(ranges.selection.endDate).toISOString(),
          key: ranges.selection.key
        },
        selected_text: TIME_DATE_SELECTED_TEXT.CUSTOM
      };
      set_timeline_filter(data);
      debouncedWritePreference(data, workspace_id);
    },
    [timeline_filter, workspace_id, debouncedWritePreference]
  );

  //for pagination-called when load more is clicked
  const handleLoadMore = useCallback(async () => {
    await loadData();
  }, [loadData]);

  const handleFilterClick = useCallback(async () => {
    resetActivityData();
    setDataLoading(true);
    await loadData();
    setDataLoading(false);
  }, [resetActivityData, loadData]);

  const renderFilterDropdown = useMemo(() => {
    return (
      <Heading>
        <LeftContent>
          <Dropdown
            modalTitle='Member'
            isMinWidth={152}
            onChangeSearch={onChangeSearchUser}
            content={
              <DropdownButton>
                <Chip
                  variant={CHIP_TYPE.FILL}
                  size={CHIP_SIZE.SMALL}
                  iconColorType='primary'
                  iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                  title='Member'
                  trailingIconName='down-arrow-icon'
                  trailingIconClassName={isCustomerDropdownOpen ? 'dropdown-icon ' : ''}
                  onClick={() => onClickonClient(!isCustomerDropdownOpen)}
                  isActive={isCustomerDropdownOpen}
                />
              </DropdownButton>
            }
            trigger='click'
            onOutsideClick={() => setIsCustomerDropdownOpen(false)}>
            {renderCustomerData}
          </Dropdown>
          <Dropdown
            modalTitle='Project'
            isMinWidth={152}
            onChangeSearch={onChangeSearchProject}
            content={
              <DropdownButton>
                <Chip
                  variant={CHIP_TYPE.FILL}
                  size={CHIP_SIZE.SMALL}
                  iconColorType='primary'
                  iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                  title='Project'
                  trailingIconName='down-arrow-icon'
                  trailingIconClassName={isProjectDropdownOpen ? 'dropdown-icon ' : ''}
                  onClick={() => onClickonProject(!isProjectDropdownOpen)}
                  isActive={isProjectDropdownOpen}
                />
              </DropdownButton>
            }
            trigger='click'
            onOutsideClick={() => setIsProjectDropdownOpen(false)}>
            {renderProjectData}
          </Dropdown>
        </LeftContent>
        <RightSection>
          <RangeSelectionSection>
            <Dropdown
              modalTitle='Custom Date'
              isMinWidth={152}
              content={
                <DropdownButton role='button' ref={buttonRef} onClick={() => openTextDropdown(!isTextDropdownOpen)}>
                  <Chip
                    variant={CHIP_TYPE.FILL}
                    size={CHIP_SIZE.SMALL}
                    iconColorType='primary'
                    iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                    title={selectedText}
                    trailingIconName='down-arrow-icon'
                    trailingIconClassName={isTextDropdownOpen ? 'dropdown-icon ' : ''}
                    className='custom-dropdown'
                    onClick={() => openTextDropdown(!isTextDropdownOpen)}
                    isActive={isTextDropdownOpen}
                  />
                </DropdownButton>
              }
              trigger='click'
              onOutsideClick={() => setIsTextDropdownOpen(false)}>
              <CustomItemDiv>
                <ReportDurationDropdown
                  buttonRef={buttonRef}
                  setSelectionRange={(value) => setSelectedRange(value)}
                  setIsStatusDropdownOpen={(value) => {}}
                  setDateSelectedText={(value) => setSelectedText(value)}
                  updateDateRangeChange={updateDateRangeChange}
                />
              </CustomItemDiv>
            </Dropdown>
            <DropdownButton isMonthPicker>
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title={`${dayjs(selectedRange?.startDate).format('MMM DD')} - ${dayjs(selectedRange?.endDate).format(
                  'MMM DD'
                )}`}
                leadingIconName='calendar-icon'
                trailingIconName='down-arrow-icon'
                className='calendar-dropdown'
                trailingIconClassName={isDateDropdownOpen ? 'dropdown-icon ' : ''}
                onClick={() => openDateDropdown(!isDateDropdownOpen)}
                isActive={isDateDropdownOpen}
              />
              {isDateDropdownOpen && (
                <DateDropBox ref={outsideRef}>
                  <DateRange
                    editableDateInputs={true}
                    onChange={handleSelect}
                    moveRangeOnFirstSelection={false}
                    ranges={[
                      {
                        startDate: new Date(selectedRange?.startDate),
                        endDate: new Date(selectedRange?.endDate),
                        key: selectedRange?.key
                      }
                    ]}
                    maxDate={new Date()}
                  />
                </DateDropBox>
              )}
            </DropdownButton>
          </RangeSelectionSection>
          <Button
            variant={BUTTON_TYPE.PRIMARY}
            buttonType='button'
            title='Apply Filter'
            size='small'
            onClick={handleFilterClick}
            disabled={loading}
          />
          {exportData.length > 0 && (
            <CSVLink data={exportData} headers={TimelineReportCSVHeader} filename='team_timeline_report.csv'>
              <Button
                variant={BUTTON_TYPE.SECONDARY}
                buttonType='button'
                title='Export as CSV'
                iconName='download-icon'
                iconPosition='start'
                size='small'
              />
            </CSVLink>
          )}
        </RightSection>
      </Heading>
    );
  }, [
    exportData,
    handleFilterClick,
    handleSelect,
    isCustomerDropdownOpen,
    isDateDropdownOpen,
    isProjectDropdownOpen,
    isTextDropdownOpen,
    loading,
    onChangeSearchProject,
    onChangeSearchUser,
    onClickonClient,
    onClickonProject,
    openDateDropdown,
    openTextDropdown,
    renderCustomerData,
    renderProjectData,
    selectedRange?.endDate,
    selectedRange?.key,
    selectedRange?.startDate,
    selectedText,
    updateDateRangeChange
  ]);

  return (
    <>
      <AppLayout>
        {!mobile ? (
          <Header isBottomBorder={true}>
            <HeaderLeft>
              <HeaderTitle>Team Timeline</HeaderTitle>
            </HeaderLeft>
            {!mobile && renderFilterDropdown}
          </Header>
        ) : (
          <ResponsiveHeader title='Team Timeline' noMargin={true} />
        )}
        <Content>
          {mobile && renderFilterDropdown}
          {(loading || dataLoading) && activities?.length === 0 && <ReportTimelineLoading />}
          {!dataLoading && (
            <TimeLineCard>
              {['Today', 'This Week', 'This Month', 'Older']
                .filter((section) => groupedActivities[section]?.length > 0)
                .map((section) => (
                  <ReportTeamTimeLine key={section} title={section} activities={groupedActivities[section]} />
                ))}
              {showLoadMoreButton && (
                <ButtonComponent>
                  <Button
                    variant={BUTTON_TYPE.SECONDARY}
                    buttonType='button'
                    title={loading ? 'Loading...' : 'Load More'}
                    size='small'
                    onClick={handleLoadMore}
                  />
                </ButtonComponent>
              )}
            </TimeLineCard>
          )}
        </Content>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};

export default observer(TeamTimeline);
